// MobileViewerActions — Aktionszeile unter der Karte im Overlay-Viewer: Menge, Preis,
// Kategorie-Wechsel, Details-Knopf und das ⋮-Kartenmenü. Bei einem fremden Deck
// (`isOwnProfile=false`) bleiben nur die Preis-Zeile und der Details-Knopf stehen — kein
// Mengen-/Verschieben-/Kartenmenü, auch keine Label-Bedienung: die reicht sonst am
// `deckCardEntryId` vorbei an Karten fremder Decks.
import React from 'react';
import { Info, ListEnd, Minus, Plus } from 'lucide-react';
import CardOverlays from '../../../../ui/CardOverlays';
import CardContextMenu from '../../components/CardContextMenu';
import { formatPrice } from '../../utils/formatPrice';

/**
 * Die Knöpfe sind `CardOverlays.Button`: dieselbe Glas-Optik wie die Blätter-Knöpfe des
 * Viewers, mit Touch-Größe auf Coarse-Pointer-Geräten.
 *
 * @param {boolean} isOwnProfile — false blendet den gesamten Aktionsbereich bis auf die
 *   Preis-Zeile und den Details-Knopf aus (fremdes Deck: reine Ansicht)
 * @param {boolean} canEdit — Menge, Print wechseln, Commander und Entfernen (Bearbeitungsrecht)
 * @param {boolean} canMove — Kategorie-Wechsel (nur in editierbaren Gruppier-Modi)
 * @param {Function} onShowDetails — „Details"-Knopf (Karten-Modal), unabhängig vom Recht; nur mit oracleId
 */
export default function MobileViewerActions({
  card,
  isOwnProfile = true,
  canEdit = false,
  canMove = false,
  deckLabels = [],
  cardLabels = [],
  onQuantityDelta,
  onMove,
  onAssignLabel,
  onRemoveLabel,
  onNewLabel,
  onShowDetails,
  onSwitchPrint,
  onToggleCommander,
  onRemove,
}) {
  if (!card) return null;
  const quantity = Number(card.quantity) > 0 ? Number(card.quantity) : 1;

  const detailsButton = typeof onShowDetails === 'function' && card.oracleId ? (
    <CardOverlays.Button icon={Info} label="Details" onClick={() => onShowDetails(card)} />
  ) : null;

  if (!isOwnProfile) {
    return (
      <div className="mobile-viewer-actions">
        <span className="mobile-viewer-actions__price">
          {formatPrice(card.price)}
          {quantity > 1 && <span className="mobile-viewer-actions__times"> ×{quantity}</span>}
        </span>
        {detailsButton}
      </div>
    );
  }

  const canLabel = Boolean(card.deckCardEntryId);
  // Ohne Bearbeitungsrecht und ohne Label-Fähigkeit bleibt kein einziger Eintrag übrig
  // (weder extraItems noch die Label-Sektion) — der Trigger entfiele dann als leeres Popup.
  const hasMenuItems = canEdit || canLabel;

  const extraItems = [];
  if (canEdit) {
    extraItems.push({
      label: card.isCommander ? 'Commander-Status entfernen' : 'Als Commander setzen',
      onClick: () => onToggleCommander?.(card, !card.isCommander),
    });
    extraItems.push({
      label: 'Entfernen',
      danger: true,
      onClick: () => onRemove?.(card),
    });
  }

  return (
    <div className="mobile-viewer-actions">
      {canEdit && (
        <span className="mobile-viewer-actions__group">
          <CardOverlays.Button
            icon={Minus}
            label="Menge verringern"
            onClick={() => onQuantityDelta?.(card, -1)}
          />
          <span className="mobile-viewer-actions__quantity">{quantity}</span>
          <CardOverlays.Button
            icon={Plus}
            label="Menge erhöhen"
            onClick={() => onQuantityDelta?.(card, 1)}
          />
        </span>
      )}
      <span className="mobile-viewer-actions__price">
        {formatPrice(card.price)}
        {quantity > 1 && <span className="mobile-viewer-actions__times"> ×{quantity}</span>}
      </span>
      {canMove && (
        <CardOverlays.Button
          icon={ListEnd}
          label="Kategorie wechseln"
          onClick={() => onMove?.(card)}
        />
      )}
      {detailsButton}
      {hasMenuItems && (
        <CardContextMenu
          overlayTrigger
          card={card}
          extraItems={extraItems}
          onSwitchPrint={canEdit && card.oracleId ? onSwitchPrint : undefined}
          deckLabels={canLabel ? deckLabels : []}
          cardLabels={cardLabels}
          onAssignLabel={onAssignLabel}
          onRemoveLabel={onRemoveLabel}
          onNewLabel={canLabel ? onNewLabel : undefined}
        />
      )}
    </div>
  );
}
